import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom'
import { customerApi, getCustomerToken } from '../../services/customerAuth'
import { useLanguage } from '../../context/LanguageContext'

const CANCELLABLE_STATUSES = ['pending', 'confirmed', 'needs_attention']

const AUTO_REFRESH_MS = 30_000

// MySQL DATE columns (no timezone) must be parsed as local-only — see the
// matching server-side fmtLocalDate helper in playStayController.js.
// `new Date('2026-04-27')` parses as UTC midnight and renders as Apr 26
// in any timezone west of UTC.
function fmtLocal(d, opts) {
  if (!d) return ''
  try {
    const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10)
    const [y, m, day] = s.split('-').map(Number)
    if (!y || !m || !day) return String(d)
    return new Date(y, m - 1, day).toLocaleDateString('en-US', opts)
  } catch { return String(d) }
}
function fmtDate(d) { return fmtLocal(d, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }
function fmtShort(d) { return fmtLocal(d, { weekday: 'short', month: 'short', day: 'numeric' }) }

function StatusChip({ state, label }) {
  const { t } = useLanguage()
  // state: 'confirmed' | 'pending' | 'failed' | 'none'
  const styles = {
    confirmed: { bg: '#dcfce7', fg: '#166534', dot: '#22c55e', text: t('sp_status_confirmed', 'Confirmed') },
    pending:   { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b', text: t('sp_status_confirming', 'Confirming…') },
    failed:    { bg: '#fee2e2', fg: '#991b1b', dot: '#ef4444', text: t('sp_kpi_needs_attention', 'Needs attention') },
    none:      { bg: '#f1f5f9', fg: '#64748b', dot: '#94a3b8', text: t('sp_na', 'N/A') },
  }
  const s = styles[state] || styles.none
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, background: s.bg, color: s.fg,
      fontSize: 12, fontWeight: 700,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
      {label || s.text}
    </span>
  )
}

function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
      marginBottom: 16, ...style,
    }}>{children}</div>
  )
}

export default function MyTrip() {
  const { t } = useLanguage()
  const { bookingId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tokenFromUrl = searchParams.get('t')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const refreshTimer = useRef(null)
  // Cancel flow state (Feature 3)
  const [cancelStage, setCancelStage] = useState(null) // null | 'preview' | 'confirming' | 'done' | 'error'
  const [cancelPreview, setCancelPreview] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelError, setCancelError] = useState(null)
  // Reschedule flow state (Feature 4)
  const [rescheduleStage, setRescheduleStage] = useState(null) // null | 'form' | 'submitting' | 'done' | 'pending'
  const [rescheduleForm, setRescheduleForm] = useState({ newPlayDate: '', newHotelCheckin: '', newHotelCheckout: '', reason: '' })
  const [rescheduleError, setRescheduleError] = useState(null)
  const [openReschedule, setOpenReschedule] = useState(null) // existing pending request, if any
  // Share state (Feature 10)
  const [shares, setShares] = useState([])
  const [shareEmail, setShareEmail] = useState('')
  const [shareCreating, setShareCreating] = useState(false)
  const [shareCopiedId, setShareCopiedId] = useState(null)

  function authConfig() {
    const params = {}
    if (tokenFromUrl) params.t = tokenFromUrl
    const accountToken = getCustomerToken()
    const config = { params }
    if (!tokenFromUrl && accountToken) config.headers = { Authorization: `Bearer ${accountToken}` }
    return config
  }

  async function load() {
    setError(null)
    try {
      const { data: res } = await customerApi.get(`/my-trip/${bookingId}`, authConfig())
      setData(res.booking)
      // Check for any open reschedule request (Feature 4)
      try {
        const { data: hist } = await customerApi.get(`/my-trip/${bookingId}/reschedule-history`, authConfig())
        const pending = (hist.history || []).find(h => ['requested', 'in_progress'].includes(h.Status))
        setOpenReschedule(pending || null)
      } catch { /* non-critical */ }
      // Load active shares (Feature 10)
      try {
        const { data: shareList } = await customerApi.get(`/my-trip/${bookingId}/shares`, authConfig())
        setShares(Array.isArray(shareList) ? shareList.filter(s => !s.RevokedAt) : [])
      } catch { /* non-critical */ }
      // If anything is still pending, schedule a refresh
      const anyPending = res.booking.hotel.status === 'pending'
        || res.booking.teeTimes.some(t => t.status !== 'confirmed')
        || res.booking.payment.status !== 'paid'
      if (anyPending) {
        clearTimeout(refreshTimer.current)
        refreshTimer.current = setTimeout(load, AUTO_REFRESH_MS)
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    return () => clearTimeout(refreshTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, tokenFromUrl])

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>{t('sp_loading_your_trip', 'Loading your trip…')}</div>
  if (error) return (
    <div style={{ padding: 40, maxWidth: 520, margin: '40px auto', textAlign: 'center' }}>
      <h2 style={{ color: '#991b1b' }}>{t('sp_cant_load_trip', 'Can\'t load your trip')}</h2>
      <p style={{ color: '#555' }}>{error}</p>
      <p style={{ fontSize: 14, color: '#888' }}>{t('sp_link_expired_contact', 'If your link has expired, please contact the course for a new one.')}</p>
    </div>
  )
  if (!data) return null

  const b = data
  const paymentStatus = b.payment.status === 'paid' ? 'confirmed' : 'pending'

  return (
    <div style={{
      minHeight: '100vh', background: '#f8fafc',
      fontFamily: "'Plus Jakarta Sans', -apple-system, system-ui, sans-serif",
    }}>
      {/* Header */}
      <div style={{ background: '#153230', color: '#fff', padding: '18px 20px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{t('sp_playstay_my_trip', 'PlayStay · My Trip')}</div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>Booking #{b.bookingId}</div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        {/* Overall status banner */}
        {b.status === 'needs_attention' && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 14, marginBottom: 16, color: '#92400e', fontSize: 14 }}>
            <strong>{t('sp_items_need_attention', 'Some items need attention.')}</strong> {' '}
            {b.needsAttentionReason?.includes('hotel') && t('sp_finalizing_hotel', 'We\'re finalizing your hotel booking — the status on this page will update automatically.')}
            {b.needsAttentionReason?.includes('teetime') && t('sp_tee_time_manual_confirm', 'A tee time needs manual confirmation — the course will contact you shortly.')}
            {!b.needsAttentionReason && t('sp_staff_reviewing', 'Our staff is reviewing your booking.')}
          </div>
        )}

        {/* Summary */}
        <Card>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#153230', marginBottom: 4 }}>{b.package.name}</div>
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
            {b.hostCourse.name}{b.package.isMultiCourse ? t('sp_plus_partner_courses', ' + partner courses') : ''} · {b.numGolfers} {b.numGolfers > 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')}
          </div>
          <div style={{ fontSize: 14, color: '#334155' }}>
            {fmtDate(b.playDate)}
          </div>
        </Card>

        {/* Status strip */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_booking_status', 'Booking status')}</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#334155' }}>{t('sp_payment_label', 'Payment')}</span>
              <StatusChip state={paymentStatus} label={paymentStatus === 'confirmed' ? t('sp_paid', 'Paid') : t('sp_status_pending', 'Pending')} />
            </div>
            {b.teeTimes.map((tt, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#334155' }}>
                  {b.teeTimes.length > 1 ? `${t('sp_round_label', 'Round')} ${i + 1}` : t('sp_tee_time_label', 'Tee time')} · {tt.courseName}
                </span>
                <StatusChip state={tt.status === 'confirmed' ? 'confirmed' : tt.status === 'failed' ? 'failed' : 'pending'} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#334155' }}>{t('sp_th_hotel', 'Hotel')} · {b.hotel.propertyName || t('sp_tbd', 'TBD')}</span>
              <StatusChip state={b.hotel.status} />
            </div>
          </div>
        </Card>

        {/* Tee times detail */}
        {b.teeTimes.length > 0 && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_golf_label', 'Golf')}</div>
            {b.teeTimes.map((tt, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i === b.teeTimes.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{tt.courseName}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  {fmtShort(tt.day)}{tt.teeTime ? ` ${t('at', 'at')} ${String(tt.teeTime).substring(0, 5)}` : ''}
                </div>
                {tt.confirmationNumber && (
                  <div style={{ fontSize: 12, color: '#166534', marginTop: 4 }}>
                    {t('sp_confirmation_label', 'Confirmation:')} <code style={{ background: '#f0fdf4', padding: '2px 6px', borderRadius: 4 }}>{tt.confirmationNumber}</code>
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {/* Hotel */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_th_hotel', 'Hotel')}</div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{b.hotel.propertyName || t('sp_to_be_confirmed', 'To be confirmed')}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {t('sp_check_in_label', 'Check-in')} {fmtShort(b.hotel.checkin)} · {t('sp_check_out_label', 'Check-out')} {fmtShort(b.hotel.checkout)}
            {b.package.hotelRooms > 1 ? ` · ${b.package.hotelRooms} ${t('sp_rooms_lower', 'rooms')}` : ''}
          </div>
          {b.hotel.status === 'pending' && (
            <div style={{ marginTop: 10, padding: 10, background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
              {t('sp_hotel_waiting_confirmation', 'We\'re waiting on your hotel\'s final confirmation number. This page will update automatically when it arrives.')}
            </div>
          )}
        </Card>

        {/* Host course contact */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 8 }}>{t('sp_questions', 'Questions?')}</div>
          <div style={{ fontSize: 14, color: '#334155' }}>{t('sp_contact_label', 'Contact')} {b.hostCourse.name}</div>
          {b.hostCourse.phone && <div style={{ fontSize: 14, color: '#334155', marginTop: 4 }}>{t('phone', 'Phone')}: <a href={`tel:${b.hostCourse.phone}`} style={{ color: '#00d19f' }}>{b.hostCourse.phone}</a></div>}
          {b.hostCourse.email && <div style={{ fontSize: 14, color: '#334155' }}>{t('email', 'Email')}: <a href={`mailto:${b.hostCourse.email}`} style={{ color: '#00d19f' }}>{b.hostCourse.email}</a></div>}
        </Card>

        {/* Add-ons (Feature 2) */}
        {b.addons && b.addons.length > 0 && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_addons', 'Add-ons')}</div>
            {b.addons.map((a, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i === b.addons.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 14, color: '#0f172a' }}>
                  {a.Name}{a.Quantity > 1 ? ` × ${a.Quantity}` : ''}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#00d19f' }}>
                  ${(a.TotalPriceCents / 100).toFixed(2)}
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Total */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 14, color: '#64748b' }}>{t('sp_total_paid', 'Total paid')}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#00d19f' }}>${b.total.toFixed(2)}</div>
          </div>
        </Card>

        {/* Per-golfer payment plan (split + installments) */}
        {b.paymentPlan && b.paymentPlan.golfers && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>
              {t('sp_payment_plan', 'Payment plan')}
              <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginLeft: 8 }}>
                {b.paymentPlan.splitType.replace(/_/g, ' ')}
              </span>
            </div>
            {b.paymentPlan.golfers.map((g, gi) => (
              <div key={gi} style={{ padding: '10px 0', borderBottom: gi === b.paymentPlan.golfers.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                  {g.name || g.email || `${t('sp_golfer_label', 'Golfer')} ${g.golferIndex}`}
                  {gi === 0 && <span style={{ marginLeft: 6, fontSize: 10, background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: 10, fontWeight: 700 }}>{t('sp_primary_caps', 'PRIMARY')}</span>}
                </div>
                {g.installments.map((inst, ii) => {
                  const label = inst.total > 1
                    ? `${t('sp_installment', 'Installment')} ${inst.index}/${inst.total}`
                    : t('sp_share_label', 'Share')
                  const statusColor = {
                    paid: { bg: '#dcfce7', fg: '#166534' },
                    pending: { bg: '#fef3c7', fg: '#92400e' },
                    failed: { bg: '#fee2e2', fg: '#991b1b' },
                    cancelled: { bg: '#f1f5f9', fg: '#64748b' },
                    expired: { bg: '#fee2e2', fg: '#991b1b' },
                  }[inst.status] || { bg: '#f1f5f9', fg: '#64748b' }
                  return (
                    <div key={ii} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 13 }}>
                      <div style={{ color: '#475569' }}>
                        {label}
                        {inst.dueDate && inst.status === 'pending' && (
                          <span style={{ color: '#92400e', fontSize: 11, marginLeft: 6 }}>
                            {t('sp_due', 'due')} {new Date(inst.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#0f172a', fontWeight: 600 }}>${inst.amountOwed.toFixed(2)}</span>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: statusColor.bg, color: statusColor.fg, textTransform: 'uppercase' }}>
                          {inst.status}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </Card>
        )}

        {/* Download PDF */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#153230' }}>{t('sp_printable_itinerary', 'Printable itinerary')}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('sp_pdf_useful_for', 'Useful at hotel check-in and the course bag drop.')}</div>
            </div>
            <a
              href={`/api/public/play-stay/my-trip/${b.bookingId}/pdf${tokenFromUrl ? `?t=${encodeURIComponent(tokenFromUrl)}` : ''}`}
              {...(tokenFromUrl ? {} : { rel: 'noopener', onClick: (e) => {
                // Account-token path: fetch with auth header and trigger download client-side
                e.preventDefault()
                const accountToken = getCustomerToken()
                if (!accountToken) return
                fetch(`/api/public/play-stay/my-trip/${b.bookingId}/pdf`, {
                  headers: { Authorization: `Bearer ${accountToken}` },
                })
                  .then(r => r.ok ? r.blob() : Promise.reject(new Error('Download failed')))
                  .then(blob => {
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `stayplay-itinerary-${b.bookingId}.pdf`
                    document.body.appendChild(a)
                    a.click()
                    a.remove()
                    URL.revokeObjectURL(url)
                  })
                  .catch(err => alert('Download failed: ' + err.message))
              }})}
              style={{
                background: '#153230', color: '#fff', padding: '10px 18px',
                textDecoration: 'none', borderRadius: 6, fontWeight: 700, fontSize: 14,
                whiteSpace: 'nowrap',
              }}
            >{t('sp_download_pdf', 'Download PDF')}</a>
          </div>
        </Card>

        {/* Share trip (Feature 10) */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 8 }}>{t('sp_share_with_companions', 'Share with companions')}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            {t('sp_share_blurb', 'Generate a view-only link. Anyone with the link sees the itinerary but can\'t change anything.')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="email" placeholder={t('sp_share_email_placeholder', 'companion@email.com (optional)')}
              value={shareEmail} onChange={e => setShareEmail(e.target.value)}
              style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
            <button
              disabled={shareCreating}
              onClick={async () => {
                setShareCreating(true)
                try {
                  const body = shareEmail ? { email: shareEmail.trim().toLowerCase() } : {}
                  const { data } = await customerApi.post(`/my-trip/${bookingId}/share`, body, authConfig())
                  setShareEmail('')
                  // Refresh list
                  const { data: refreshed } = await customerApi.get(`/my-trip/${bookingId}/shares`, authConfig())
                  setShares(Array.isArray(refreshed) ? refreshed.filter(s => !s.RevokedAt) : [])
                  // Auto-copy the new link
                  try { await navigator.clipboard.writeText(data.shareUrl); setShareCopiedId(data.shareId) } catch {}
                  setTimeout(() => setShareCopiedId(null), 3000)
                } catch (err) {
                  alert(err.response?.data?.message || t('sp_share_create_failed', 'Share creation failed'))
                } finally { setShareCreating(false) }
              }}
              style={{ padding: '8px 16px', background: '#00d19f', color: '#153230', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
              {shareCreating ? t('sp_creating_ellipsis', 'Creating…') : t('sp_create_link', 'Create link')}
            </button>
          </div>
          {shareCopiedId && (
            <div style={{ fontSize: 12, color: '#166534', padding: 6, background: '#dcfce7', borderRadius: 4, marginBottom: 8 }}>
              {t('sp_link_copied', 'Link copied to clipboard!')}
            </div>
          )}
          {shares.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>
              {shares.map(s => (
                <div key={s.ID_NO} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12 }}>
                      {s.SharedWithEmail || t('sp_link_only', '(link only)')}
                      {s.ViewCount > 0 && <span style={{ marginLeft: 6, color: '#94a3b8' }}>· {t('sp_viewed', 'viewed')} {s.ViewCount}×</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>
                      {t('sp_expires_label', 'Expires')} {new Date(s.ExpiresAt).toLocaleDateString()} ·
                      <a href={s.shareUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#00d19f', marginLeft: 6 }}>{t('sp_open_link', 'open')}</a>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(t('sp_revoke_share_confirm', 'Revoke this share link?'))) return
                      try {
                        await customerApi.post(`/my-trip/${bookingId}/shares/${s.ID_NO}/revoke`, {}, authConfig())
                        setShares(shares.filter(x => x.ID_NO !== s.ID_NO))
                      } catch (err) { alert(err.response?.data?.message || t('sp_revoke_failed', 'Revoke failed')) }
                    }}
                    style={{ padding: '4px 10px', background: '#fff', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>{t('sp_revoke', 'Revoke')}</button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Reschedule (Feature 4) */}
        {openReschedule && (
          <Card style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>{t('sp_reschedule_in_progress', 'Reschedule request in progress')}</div>
            <div style={{ fontSize: 13, color: '#92400e' }}>
              {t('sp_reschedule_requested_on', 'You requested new dates on')} {new Date(openReschedule.RequestedAt).toLocaleDateString()}. {t('sp_reschedule_staff_working', 'Staff are working on it and will email you within 24 hours.')}
            </div>
          </Card>
        )}

        {!openReschedule && CANCELLABLE_STATUSES.includes(b.status) && rescheduleStage === null && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#153230' }}>{t('sp_need_different_dates', 'Need different dates?')}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('sp_request_reschedule_blurb', 'Request a reschedule and staff will coordinate the change.')}</div>
              </div>
              <button
                onClick={() => {
                  setRescheduleError(null)
                  // Default the picker to Round 1 (or just `1` for legacy
                  // single-round bookings). Customer can switch to a later
                  // round if the trip is multi-day.
                  const defaultRound = b.teeTimes?.[0]
                  setRescheduleForm({
                    roundIndex: b.teeTimes?.length > 1 ? (defaultRound?.roundIndex || 1) : null,
                    newPlayDate: defaultRound?.day ? String(defaultRound.day).slice(0, 10) : (b.playDate ? String(b.playDate).slice(0, 10) : ''),
                    newHotelCheckin: b.hotel?.checkin ? String(b.hotel.checkin).slice(0, 10) : '',
                    newHotelCheckout: b.hotel?.checkout ? String(b.hotel.checkout).slice(0, 10) : '',
                    reason: '',
                  })
                  setRescheduleStage('form')
                }}
                style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', padding: '10px 18px', borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >{t('sp_request_reschedule', 'Request reschedule')}</button>
            </div>
          </Card>
        )}

        {rescheduleStage === 'form' && (
          <Card>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#153230', marginBottom: 12 }}>{t('sp_new_dates', 'New dates')}</div>
            {rescheduleError && (
              <div style={{ color: '#991b1b', padding: 8, background: '#fee2e2', borderRadius: 6, marginBottom: 10 }}>{rescheduleError}</div>
            )}
            {/* Round selector — only relevant for multi-day packages so we
                hide it for single-round trips. Picking a round pre-fills the
                date input with that round's current date. */}
            {b.teeTimes?.length > 1 && (
              <>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 4 }}>{t('sp_which_round', 'Which round?')}</label>
                <select
                  value={rescheduleForm.roundIndex || ''}
                  onChange={e => {
                    const idx = Number(e.target.value)
                    const round = b.teeTimes.find(tt => tt.roundIndex === idx)
                    setRescheduleForm(f => ({
                      ...f,
                      roundIndex: idx,
                      newPlayDate: round?.day ? String(round.day).slice(0, 10) : f.newPlayDate,
                    }))
                  }}
                  style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 8, boxSizing: 'border-box' }}
                >
                  {b.teeTimes.map((tt, i) => (
                    <option key={tt.roundIndex || i} value={tt.roundIndex || i + 1}>
                      {t('sp_round_label', 'Round')} {tt.roundIndex || i + 1} — {t('sp_currently', 'currently')} {fmtShort(tt.day)}{tt.teeTime ? ` ${t('at', 'at')} ${String(tt.teeTime).substring(0, 5)}` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 4 }}>{t('sp_new_tee_date', 'New tee date')}</label>
            <input type="date" value={rescheduleForm.newPlayDate}
              onChange={e => setRescheduleForm(f => ({ ...f, newPlayDate: e.target.value }))}
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 8, boxSizing: 'border-box' }} />
            {b.hotel?.propertyName && (
              <>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 4 }}>{t('sp_new_hotel_checkin', 'New hotel check-in')}</label>
                <input type="date" value={rescheduleForm.newHotelCheckin}
                  onChange={e => setRescheduleForm(f => ({ ...f, newHotelCheckin: e.target.value }))}
                  style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 8, boxSizing: 'border-box' }} />
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 4 }}>{t('sp_new_hotel_checkout', 'New hotel check-out')}</label>
                <input type="date" value={rescheduleForm.newHotelCheckout}
                  onChange={e => setRescheduleForm(f => ({ ...f, newHotelCheckout: e.target.value }))}
                  style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 8, boxSizing: 'border-box' }} />
              </>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 4 }}>{t('sp_reason_optional', 'Reason (optional)')}</label>
            <textarea value={rescheduleForm.reason}
              onChange={e => setRescheduleForm(f => ({ ...f, reason: e.target.value }))}
              placeholder={t('sp_reason_placeholder', 'What\'s prompting the change? (helps staff prioritize)')}
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', minHeight: 60, boxSizing: 'border-box', marginBottom: 12 }} />
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
              {t('sp_reschedule_disclaimer', 'Once submitted, staff will coordinate with the course and hotel. New dates may not be guaranteed — staff will confirm or propose alternatives within 24 hours.')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setRescheduleStage(null); setRescheduleError(null) }}
                style={{ flex: 1, padding: 10, background: '#fff', color: '#153230', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{t('cancel', 'Cancel')}</button>
              <button
                onClick={async () => {
                  setRescheduleStage('submitting')
                  try {
                    const body = {}
                    // Per-round reschedule: send roundIndex so the server
                    // knows which round to flag, and compare the new date
                    // against the SELECTED round's current date (not the
                    // booking's PlayDate, which only reflects Round 1).
                    const round = rescheduleForm.roundIndex
                      ? b.teeTimes?.find(tt => tt.roundIndex === rescheduleForm.roundIndex)
                      : null
                    if (rescheduleForm.roundIndex) body.roundIndex = rescheduleForm.roundIndex
                    const currentRoundDate = round?.day ? String(round.day).slice(0, 10) : String(b.playDate).slice(0, 10)
                    if (rescheduleForm.newPlayDate && rescheduleForm.newPlayDate !== currentRoundDate) body.newPlayDate = rescheduleForm.newPlayDate
                    if (rescheduleForm.newHotelCheckin && rescheduleForm.newHotelCheckin !== String(b.hotel?.checkin).slice(0, 10)) body.newHotelCheckin = rescheduleForm.newHotelCheckin
                    if (rescheduleForm.newHotelCheckout && rescheduleForm.newHotelCheckout !== String(b.hotel?.checkout).slice(0, 10)) body.newHotelCheckout = rescheduleForm.newHotelCheckout
                    if (rescheduleForm.reason) body.reason = rescheduleForm.reason
                    if (Object.keys(body).filter(k => k !== 'reason').length === 0) {
                      setRescheduleError(t('sp_pick_one_new_date', 'Pick at least one new date'))
                      setRescheduleStage('form')
                      return
                    }
                    await customerApi.post(`/my-trip/${bookingId}/reschedule`, body, authConfig())
                    setRescheduleStage('done')
                    load()
                  } catch (err) {
                    setRescheduleError(err.response?.data?.message || err.message)
                    setRescheduleStage('form')
                  }
                }}
                style={{ flex: 1, padding: 10, background: '#00d19f', color: '#153230', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>{t('sp_submit_request', 'Submit request')}</button>
            </div>
          </Card>
        )}

        {rescheduleStage === 'submitting' && (
          <Card><div style={{ textAlign: 'center', color: '#64748b' }}>{t('sp_submitting_request', 'Submitting your request…')}</div></Card>
        )}

        {rescheduleStage === 'done' && (
          <Card style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#166534', marginBottom: 4 }}>{t('sp_request_submitted', 'Request submitted')}</div>
            <div style={{ fontSize: 13, color: '#166534' }}>{t('sp_request_submitted_blurb', 'Staff will reach out within 24 hours with confirmation or alternative dates. We\'ve also emailed you a copy of your request.')}</div>
          </Card>
        )}

        {/* Cancel trip (Feature 3) */}
        {CANCELLABLE_STATUSES.includes(b.status) && cancelStage === null && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#153230' }}>{t('sp_cancel_this_trip', 'Cancel this trip')}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('sp_cancel_see_refund', 'See your refund eligibility before deciding.')}</div>
              </div>
              <button
                onClick={async () => {
                  setCancelError(null)
                  setCancelStage('preview')
                  try {
                    const { data: pv } = await customerApi.get(`/my-trip/${bookingId}/refund-preview`, authConfig())
                    setCancelPreview(pv)
                  } catch (err) {
                    setCancelError(err.response?.data?.message || err.message)
                  }
                }}
                style={{ background: '#fff', color: '#991b1b', border: '1px solid #fecaca', padding: '10px 18px', borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >{t('sp_cancel_trip', 'Cancel trip')}</button>
            </div>
          </Card>
        )}

        {cancelStage === 'preview' && (
          <Card style={{ borderColor: '#fca5a5', background: '#fef2f2' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#991b1b', marginBottom: 10 }}>{t('sp_refund_preview', 'Refund preview')}</div>
            {cancelError && (
              <div style={{ color: '#991b1b', padding: 8, background: '#fff', borderRadius: 6, marginBottom: 10 }}>{cancelError}</div>
            )}
            {!cancelPreview && !cancelError && <div style={{ color: '#64748b' }}>{t('sp_computing_refund', 'Computing your eligible refund…')}</div>}
            {cancelPreview && (
              <>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                  {t('sp_days_before_tee', 'Days before first tee time:')} <strong>{cancelPreview.daysBefore}</strong>
                </div>
                {cancelPreview.course?.perCourse?.map((pc, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                    {t('sp_course_refund_label', 'Course refund:')} {pc.tier?.matched ? `${pc.tier.percent}% (${t('sp_matched_tier', 'matched')} ${pc.tier.daysBefore}+ ${t('sp_days_tier', 'days tier')})` : t('sp_no_refund_tier', 'No refund tier matched — please contact the course')}
                  </div>
                ))}
                {cancelPreview.hotel && (
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
                    {t('sp_th_hotel', 'Hotel')}: {cancelPreview.hotel.eligible ? `${t('sp_refundable', 'Refundable')}${cancelPreview.hotel.freeCancelUntil ? ` ${t('sp_until', 'until')} ${new Date(cancelPreview.hotel.freeCancelUntil).toLocaleDateString()}` : ''}` : `${t('sp_non_refundable', 'Non-refundable')} (${cancelPreview.hotel.reason || t('sp_unknown', 'unknown')})`}
                  </div>
                )}
                <textarea placeholder={t('sp_reason_for_cancelling', 'Reason for cancelling (optional)')} value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  style={{ width: '100%', padding: 8, border: '1px solid #fecaca', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', marginBottom: 10, minHeight: 60, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#92400e', marginBottom: 12 }}>
                  {t('sp_cancel_disclaimer', 'Once you confirm, the cancellation begins immediately. Your refund will appear here as it processes (typically 5-10 business days).')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => { setCancelStage(null); setCancelPreview(null); setCancelError(null); setCancelReason('') }}
                    style={{ flex: 1, padding: 10, background: '#fff', color: '#153230', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                  >{t('sp_keep_the_trip', 'Keep the trip')}</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(t('sp_cancel_confirm', 'Cancel this trip? This cannot be undone.'))) return
                      setCancelStage('confirming')
                      try {
                        await customerApi.post(`/my-trip/${bookingId}/cancel`, { reason: cancelReason || 'customer_requested' }, authConfig())
                        setCancelStage('done')
                        // Reload booking to reflect new status
                        load()
                      } catch (err) {
                        setCancelError(err.response?.data?.message || err.message)
                        setCancelStage('preview')
                      }
                    }}
                    style={{ flex: 1, padding: 10, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}
                  >{t('sp_confirm_cancel', 'Confirm cancel')}</button>
                </div>
              </>
            )}
          </Card>
        )}

        {cancelStage === 'confirming' && (
          <Card><div style={{ textAlign: 'center', color: '#64748b' }}>{t('sp_cancelling_trip', 'Cancelling your trip…')}</div></Card>
        )}

        {cancelStage === 'done' && (
          <Card style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#166534', marginBottom: 4 }}>{t('sp_cancellation_submitted', 'Cancellation submitted')}</div>
            <div style={{ fontSize: 13, color: '#166534' }}>{t('sp_cancellation_submitted_blurb', 'Your refund is being processed. The status above will update as it progresses (typically 5-10 business days). You\'ll receive an email when the refund is issued.')}</div>
          </Card>
        )}

        {/* Account CTA */}
        {!b.hasAccount && (
          <Card style={{ background: 'linear-gradient(135deg, #00d19f 0%, #0ea885 100%)', color: '#fff', border: 'none' }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{t('sp_create_playstay_account', 'Create a PlayStay account')}</div>
            <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 12 }}>
              {t('sp_create_account_blurb', 'Manage all your trips in one place. We\'ll automatically add this trip and any future bookings to your account.')}
            </div>
            <button
              onClick={() => navigate(`/customer/signup?email=${encodeURIComponent(b.primary.email)}`)}
              style={{
                background: '#fff', color: '#153230', border: 'none', padding: '10px 20px',
                borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 14,
              }}
            >{t('sp_create_my_account', 'Create my account')}</button>
          </Card>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#94a3b8' }}>
          {t('sp_page_auto_updates', 'This page updates automatically while any status is pending.')}
        </div>
      </div>
    </div>
  )
}
